import React, { useEffect, useState } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { format, parseISO, isSameMonth, isSameDay } from 'date-fns';

import { FaCalendarAlt, FaUserCheck, FaSignInAlt, FaSignOutAlt, FaTimes, FaCheckCircle, FaClock, FaClipboardList, FaEnvelope, FaPhone, FaBuilding, FaUser, FaIdCard, FaSpinner, FaInfoCircle, FaCalendarPlus, FaEdit, FaQuestionCircle } from 'react-icons/fa';

function AttendancePage() {
  const [allAttendances, setAllAttendances] = useState([]);
  const [filteredAttendances, setFilteredAttendances] = useState([]);
  const [employeeId, setEmployeeId] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    present: 0,
    late: 0,
    absent: 0,
    total: 0,
    workedHours: 0
  });
  const [selectedAttendance, setSelectedAttendance] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [latestAttendance, setLatestAttendance] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [leaveData, setLeaveData] = useState({
    startDate: "",
    endDate: "",
    reason: "",
    type: "Sick Leave"
  });
  const [developerdata, setDeveloper] = useState(null);

  // Fetch employee ID from token
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setEmployeeId(decoded.id);
      } catch (err) {
        console.error("Token decode error:", err);
      }
    }
  }, []);

  // Fetch all data when employeeId, month, or year changes
  useEffect(() => {
    if (!employeeId) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const [attendanceRes, developerRes] = await Promise.all([
          axios.get(`http://localhost:5890/developer/attendances/${employeeId}`),
          axios.get(`http://localhost:5890/developer/api/${employeeId}`)
        ]);

        const sortedAttendances = attendanceRes.data.attendances.sort((a, b) =>
          new Date(b.LoginDate) - new Date(a.LoginDate)
        );
        setAllAttendances(sortedAttendances);
        setLatestAttendance(sortedAttendances[0]);
        setDeveloper(developerRes.data);

        filterDataByMonth(sortedAttendances, currentMonth, currentYear);
      } catch (err) {
        console.error("Failed to fetch data:", err);
        setMessage("Failed to load attendance or employee data.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [employeeId, currentMonth, currentYear]);

  const filterDataByMonth = (attendances, month, year) => {
    const filtered = attendances.filter(att => {
      if (!att.LoginDate) return false;
      const date = parseISO(att.LoginDate);
      return isSameMonth(date, new Date(year, month));
    });

    setFilteredAttendances(filtered);
    calculateStats(filtered);
  };

  const calculateStats = (attendances) => {
    const present = attendances.filter(a => a.developer_status === "Present").length;
    const late = attendances.filter(a => a.developer_status === "Late").length;
    const absent = attendances.filter(a => a.developer_status === "Absent").length;

    let totalHours = 0;
    attendances.forEach(att => {
      if (att.login_in_time && att.logout_out_time) {
        const login = new Date(att.login_in_time);
        const logout = new Date(att.logout_out_time);
        totalHours += (logout - login) / (1000 * 60 * 60);
      }
    });

    setStats({
      present,
      late,
      absent,
      total: attendances.length,
      workedHours: totalHours.toFixed(1)
    });
  };

  const handleCheckInOut = async () => {
    if (!employeeId) {
      setMessage("Employee ID not found. Please log in again.");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.post("http://localhost:5890/developer/attentneces", {
        employee_ID: employeeId
      });
      setMessage(res.data.message);

      // Refresh attendance data
      const updatedRes = await axios.get(`http://localhost:5890/developer/attendances/${employeeId}`);
      const sortedAttendances = updatedRes.data.attendances.sort((a, b) =>
        new Date(b.LoginDate) - new Date(a.LoginDate)
      );
      setAllAttendances(sortedAttendances);
      setLatestAttendance(sortedAttendances[0]);
      filterDataByMonth(sortedAttendances, currentMonth, currentYear);
    } catch (err) {
      console.error("Attendance error:", err);
      setMessage(err.response?.data?.message || "Attendance failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveRequest = () => {
    setShowLeaveModal(true);
  };

  const handleLeaveSubmit = async (e) => {
    e.preventDefault();
    if (!employeeId) {
      setMessage("Employee ID not found. Cannot submit leave request.");
      return;
    }

    // Basic validation
    if (!leaveData.startDate || !leaveData.endDate || !leaveData.reason) {
      setMessage("Please fill in all leave request fields.");
      return;
    }
    if (new Date(leaveData.startDate) > new Date(leaveData.endDate)) {
      setMessage("Start date cannot be after end date.");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.post("http://localhost:5890/attendance", {
        employee_ID: employeeId,
        absentdatefrom: leaveData.startDate,
        absentdateto: leaveData.endDate,
        absendtype: leaveData.type,
        absentreson: leaveData.reason
      });
      setMessage(res.data.message || "Leave request submitted successfully!");
      setShowLeaveModal(false);
      setLeaveData({
        startDate: "",
        endDate: "",
        reason: "",
        type: "Sick Leave"
      });
      // Optionally, refresh attendance data to show the new leave request
      const updatedRes = await axios.get(`http://localhost:5890/developer/attendances/${employeeId}`);
      const sortedAttendances = updatedRes.data.attendances.sort((a, b) =>
        new Date(b.LoginDate) - new Date(a.LoginDate)
      );
      setAllAttendances(sortedAttendances);
      setLatestAttendance(sortedAttendances[0]);
      filterDataByMonth(sortedAttendances, currentMonth, currentYear);

    } catch (err) {
      console.error("Leave request error:", err);
      setMessage(err.response?.data?.message || "Leave request failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveChange = (e) => {
    const { name, value } = e.target;
    setLeaveData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleMonthChange = (increment) => {
    const newDate = new Date(currentYear, currentMonth + increment, 1);
    setCurrentMonth(newDate.getMonth());
    setCurrentYear(newDate.getFullYear());
  };

  const openAttendanceDetails = (attendance) => {
    setSelectedAttendance(attendance);
    setShowModal(true);
  };

  const getStatusFilteredAttendances = () => {
    if (filterStatus === "all") return filteredAttendances;
    return filteredAttendances.filter(att => att.developer_status === filterStatus);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Present": return { bg: "#f0fff4", text: "#2f855a" };
      case "Late": return { bg: "#fffff0", text: "#b7791f" };
      case "Absent": return { bg: "#fff5f5", text: "#c53030" };
      default: return { bg: "#edf2f7", text: "#4a5568" };
    }
  };

  const getAbsentDates = (from, to) => {
    const dates = [];
    let current = new Date(from);
    const end = new Date(to);

    while (current <= end) {
      dates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return dates;
  };

  const tdStyle = {
    padding: '1rem',
    color: '#4a5568',
    fontSize: '0.875rem',
    borderBottom: '1px solid #e2e8f0'
  };

  const buttonStyle = {
    padding: '0.5rem 1rem',
    borderRadius: '0.375rem',
    border: '1px solid #a0aec0',
    backgroundColor: 'transparent',
    color: '#4a5568',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '500'
  };
  
  const modalButtonPrimaryStyle = {
    padding: '0.75rem 1.5rem',
    borderRadius: '0.5rem',
    fontWeight: '500',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    background: 'linear-gradient(135deg, #4299e1 0%, #3182ce 100%)',
    boxShadow: '0 4px 6px rgba(66, 153, 225, 0.3)',
    transition: 'all 0.3s ease'
  };
  
  const modalButtonSecondaryStyle = {
    padding: '0.75rem 1.5rem',
    borderRadius: '0.5rem',
    fontWeight: '500',
    color: '#4a5568',
    border: '1px solid #e2e8f0',
    backgroundColor: 'white',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  };
  
  const modalInputStyle = {
    width: '100%',
    padding: '0.75rem 1rem',
    border: '1px solid #e2e8f0',
    borderRadius: '0.375rem',
    fontSize: '1rem',
    backgroundColor: '#fff',
    transition: 'all 0.2s ease',
    outline: 'none',
  };
  
  const modalLabelStyle = {
    display: 'block',
    color: '#4a5568',
    fontSize: '0.875rem',
    fontWeight: '500',
    marginBottom: '0.5rem'
  };
  

  return (
    <div style={{
      backgroundColor: '#f5f7fa',
      minHeight: '100vh',
      padding: '2rem'
    }}>
      {/* Main Container */}
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
        {/* Header Section */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          marginBottom: '2rem'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '1rem'
          }}>
            <div>
              <h1 style={{
                fontSize: '2rem',
                fontWeight: '700',
                color: '#1a202c',
                margin: 0,
                lineHeight: '1.2'
              }}>Attendance Dashboard</h1>
              <p style={{
                color: '#4a5568',
                marginTop: '0.5rem',
                fontSize: '0.95rem'
              }}>Track and manage your attendance records</p>
            </div>
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: 'white',
                borderRadius: '0.5rem',
                padding: '0.5rem',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                <button
                  onClick={() => handleMonthChange(-1)}
                  style={{
                    padding: '0.25rem',
                    borderRadius: '50%',
                    border: 'none',
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease',
                    color: '#4a5568'
                  }}
                >
                  <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                  </svg>
                </button>
                <span style={{
                  margin: '0 1rem',
                  fontWeight: '500',
                  color: '#1a202c',
                  minWidth: '180px',
                  textAlign: 'center'
                }}>
                  {new Date(currentYear, currentMonth).toLocaleString('default', { month: 'long', year: 'numeric' })}
                </span>
                <button
                  onClick={() => handleMonthChange(1)}
                  style={{
                    padding: '0.25rem',
                    borderRadius: '50%',
                    border: 'none',
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease',
                    color: '#4a5568'
                  }}
                >
                  <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                  </svg>
                </button>
              </div>

              <button
                onClick={handleCheckInOut}
                disabled={loading || !employeeId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '0.5rem',
                  fontWeight: '500',
                  color: 'white',
                  border: 'none',
                  cursor: loading || !employeeId ? 'not-allowed' : 'pointer',
                  background: loading || !employeeId ?
                    '#a0aec0' :
                    'linear-gradient(135deg, #4299e1 0%, #3182ce 100%)',
                  boxShadow: '0 4px 6px rgba(66, 153, 225, 0.3)',
                  transition: 'all 0.3s ease'
                }}
              >
                {loading ? (
                  <>
                    <FaSpinner style={{ animation: 'spin 1s linear infinite', marginRight: '0.75rem' }} />
                    Processing...
                  </>
                ) : (
                  <>
                    <FaSignInAlt style={{ marginRight: '0.5rem' }} />
                    {
                      latestAttendance &&
                      latestAttendance.LoginDate &&
                      isSameDay(parseISO(latestAttendance.LoginDate), new Date()) &&
                      latestAttendance.login_in_time &&
                      !latestAttendance.logout_out_time
                        ? "Log Out"
                        : "Log In"
                    }
                  </>
                )}
              </button>

              <button
                onClick={handleLeaveRequest}
                disabled={loading || !employeeId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '0.5rem',
                  fontWeight: '500',
                  color: 'white',
                  border: 'none',
                  cursor: loading || !employeeId ? 'not-allowed' : 'pointer',
                  background: loading || !employeeId ?
                    '#a0aec0' :
                    'linear-gradient(135deg, #da8132 0%, #b7791f 100%)',
                  boxShadow: '0 4px 6px rgba(183, 121, 31, 0.3)',
                  transition: 'all 0.3s ease'
                }}
              >
                <FaCalendarPlus style={{ marginRight: '0.5rem' }} />
                Leave Request
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: '1.5rem',
          marginBottom: '2rem'
        }}>
          {/* Total Records Card */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            borderLeft: '4px solid #4299e1',
            boxShadow: '0 4px 6px rgba(0,0,0,0.05)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <p style={{
                  color: '#718096',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  margin: 0
                }}>Total Records</p>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  color: '#1a202c',
                  margin: '0.25rem 0 0 0'
                }}>{stats.total}</h3>
                <p style={{
                  color: '#a0aec0',
                  fontSize: '0.75rem',
                  margin: '0.25rem 0 0 0'
                }}>This month</p>
              </div>
              <div style={{
                backgroundColor: '#ebf8ff',
                padding: '0.75rem',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <FaClipboardList color="#4299e1" size={24} />
              </div>
            </div>
            <div style={{ marginTop: '1rem' }}>
              <div style={{
                height: '4px',
                width: '100%',
                backgroundColor: '#e2e8f0',
                borderRadius: '2px'
              }}>
                <div
                  style={{
                    height: '100%',
                    backgroundColor: '#4299e1',
                    borderRadius: '2px',
                    width: `${stats.total ? (stats.total / 31) * 100 : 0}%`
                  }}
                ></div>
              </div>
            </div>
          </div>

          {/* Present Days Card */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            borderLeft: '4px solid #48bb78',
            boxShadow: '0 4px 6px rgba(0,0,0,0.05)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <p style={{
                  color: '#718096',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  margin: 0
                }}>Present Days</p>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  color: '#1a202c',
                  margin: '0.25rem 0 0 0'
                }}>{stats.present}</h3>
                <p style={{
                  color: '#a0aec0',
                  fontSize: '0.75rem',
                  margin: '0.25rem 0 0 0'
                }}>
                  {stats.total ? Math.round((stats.present / stats.total) * 100) : 0}% of total
                </p>
              </div>
              <div style={{
                backgroundColor: '#f0fff4',
                padding: '0.75rem',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <FaCheckCircle color="#48bb78" size={24} />
              </div>
            </div>
            <div style={{ marginTop: '1rem' }}>
              <div style={{
                height: '4px',
                width: '100%',
                backgroundColor: '#e2e8f0',
                borderRadius: '2px'
              }}>
                <div
                  style={{
                    height: '100%',
                    backgroundColor: '#48bb78',
                    borderRadius: '2px',
                    width: `${stats.total ? (stats.present / stats.total) * 100 : 0}%`
                  }}
                ></div>
              </div>
            </div>
          </div>

          {/* Late Days Card */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            borderLeft: '4px solid #d69e2e',
            boxShadow: '0 4px 6px rgba(0,0,0,0.05)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <p style={{
                  color: '#718096',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  margin: 0
                }}>Late Days</p>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  color: '#1a202c',
                  margin: '0.25rem 0 0 0'
                }}>{stats.late}</h3>
                <p style={{
                  color: '#a0aec0',
                  fontSize: '0.75rem',
                  margin: '0.25rem 0 0 0'
                }}>
                  {stats.total ? Math.round((stats.late / stats.total) * 100) : 0}% of total
                </p>
              </div>
              <div style={{
                backgroundColor: '#fffff0',
                padding: '0.75rem',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <FaClock color="#d69e2e" size={24} />
              </div>
            </div>
            <div style={{ marginTop: '1rem' }}>
              <div style={{
                height: '4px',
                width: '100%',
                backgroundColor: '#e2e8f0',
                borderRadius: '2px'
              }}>
                <div
                  style={{
                    height: '100%',
                    backgroundColor: '#d69e2e',
                    borderRadius: '2px',
                    width: `${stats.total ? (stats.late / stats.total) * 100 : 0}%`
                  }}
                ></div>
              </div>
            </div>
          </div>

          {/* Absent Days Card */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            borderLeft: '4px solid #e53e3e',
            boxShadow: '0 4px 6px rgba(0,0,0,0.05)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <p style={{
                  color: '#718096',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  margin: 0
                }}>Absent Days</p>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  color: '#1a202c',
                  margin: '0.25rem 0 0 0'
                }}>{stats.absent}</h3>
                <p style={{
                  color: '#a0aec0',
                  fontSize: '0.75rem',
                  margin: '0.25rem 0 0 0'
                }}>
                  {stats.total ? Math.round((stats.absent / stats.total) * 100) : 0}% of total
                </p>
              </div>
              <div style={{
                backgroundColor: '#fff5f5',
                padding: '0.75rem',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <FaTimes color="#e53e3e" size={24} />
              </div>
            </div>
            <div style={{ marginTop: '1rem' }}>
              <div style={{
                height: '4px',
                width: '100%',
                backgroundColor: '#e2e8f0',
                borderRadius: '2px'
              }}>
                <div
                  style={{
                    height: '100%',
                    backgroundColor: '#e53e3e',
                    borderRadius: '2px',
                    width: `${stats.total ? (stats.absent / stats.total) * 100 : 0}%`
                  }}
                ></div>
              </div>
            </div>
          </div>

          {/* Worked Hours Card */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            borderLeft: '4px solid #9f7aea',
            boxShadow: '0 4px 6px rgba(0,0,0,0.05)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <p style={{
                  color: '#718096',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  margin: 0
                }}>Worked Hours</p>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  color: '#1a202c',
                  margin: '0.25rem 0 0 0'
                }}>{stats.workedHours}</h3>
                <p style={{
                  color: '#a0aec0',
                  fontSize: '0.75rem',
                  margin: '0.25rem 0 0 0'
                }}>
                  This month
                </p>
              </div>
              <div style={{
                backgroundColor: '#faf5ff',
                padding: '0.75rem',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <FaClock color="#9f7aea" size={24} />
              </div>
            </div>
            <div style={{ marginTop: '1rem' }}>
              <div style={{
                height: '4px',
                width: '100%',
                backgroundColor: '#e2e8f0',
                borderRadius: '2px'
              }}>
                <div
                  style={{
                    height: '100%',
                    backgroundColor: '#9f7aea',
                    borderRadius: '2px',
                    width: `${Math.min((stats.workedHours / (stats.present > 0 ? stats.present * 8 : 1)) * 100, 100)}%`
                  }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Message Alert */}
        {message && (
          <div style={{
            marginBottom: '1.5rem',
            padding: '1rem',
            borderRadius: '0.5rem',
            backgroundColor: message.includes("success") ? '#f0fff4' : (message.includes("failed") ? '#fff5f5' : '#ebf8ff'),
            color: message.includes("success") ? '#2f855a' : (message.includes("failed") ? '#c53030' : '#2b6cb0'),
            border: `1px solid ${message.includes("success") ? '#c6f6d5' : (message.includes("failed") ? '#fed7d7' : '#bee3f8')}`,
            display: 'flex',
            alignItems: 'center'
          }}>
            <FaInfoCircle style={{ marginRight: '0.75rem', flexShrink: 0 }} />
            <span style={{ fontWeight: '500', flex: 1 }}>{message}</span>
            <button
              onClick={() => setMessage("")}
              style={{
                padding: '0.25rem',
                borderRadius: '50%',
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: message.includes("success") ? '#2f855a' : (message.includes("failed") ? '#c53030' : '#2b6cb0')
              }}
            >
              <FaTimes />
            </button>
          </div>
        )}

        {/* Employee Info and Attendance Table */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: '2rem',
          '@media (min-width: 1024px)': {
            gridTemplateColumns: '1fr 2fr'
          }
        }}>
          {/* Employee Info Card */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0.75rem',
            overflow: 'hidden',
            boxShadow: '0 4px 6px rgba(0,0,0,0.05)'
          }}>
            <div style={{
              padding: '1.5rem',
              background: 'linear-gradient(135deg, #ebf4ff 0%, #c3dafe 100%)',
              borderBottom: '1px solid #e2e8f0'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center'
              }}>
                <div style={{
                  backgroundColor: 'white',
                  padding: '0.75rem',
                  borderRadius: '50%',
                  marginRight: '1rem',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                }}>
                  <FaUserCheck color="#4299e1" size={24} />
                </div>
                <div>
                  <h2 style={{
                    fontSize: '1.25rem',
                    fontWeight: '600',
                    color: '#1a202c',
                    margin: 0
                  }}>Employee Information</h2>
                </div>
              </div>
            </div>

            <div style={{ padding: '1.5rem' }}>
              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                  <FaSpinner style={{
                    animation: 'spin 1s linear infinite',
                    color: '#4299e1',
                    fontSize: '3rem'
                  }} />
                </div>
              ) : developerdata ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2rem' }}>
                  {/* Column 1 */}
                  <div style={{ flex: '1 1 45%' }}>
                    <div style={{ marginBottom: '1rem' }}>
                      <p style={{ fontSize: '0.875rem', color: '#718096', margin: 0 }}><FaUser style={{ marginRight: '0.5rem' }} />Name:</p>
                      <p style={{ fontSize: '1rem', fontWeight: '600', color: '#1a202c', margin: '0.25rem 0 0 0' }}>{developerdata.developerName}</p>
                    </div>
                   
                    <div style={{ marginBottom: '1rem' }}>
                      <p style={{ fontSize: '0.875rem', color: '#718096', margin: 0 }}><FaPhone style={{ marginRight: '0.5rem' }} />Phone:</p>
                      <p style={{ fontSize: '1rem', fontWeight: '600', color: '#1a202c', margin: '0.25rem 0 0 0' }}>{developerdata.developerMobileNumber || 'N/A'}</p>
                    </div>
                  </div>
                  {/* Column 2 */}
                  <div style={{ flex: '1 1 45%' }}>
                    <div style={{ marginBottom: '1rem' }}>
                      <p style={{ fontSize: '0.875rem', color: '#718096', margin: 0 }}><FaEnvelope style={{ marginRight: '0.5rem' }} />Email:</p>
                      <p style={{ fontSize: '1rem', fontWeight: '600', color: '#1a202c', margin: '0.25rem 0 0 0' }}>{developerdata.developerEmail || 'N/A'}</p>
                    </div>
                   
                    <div>
                      <p style={{ fontSize: '0.875rem', color: '#718096', margin: 0 }}><FaEdit style={{ marginRight: '0.5rem' }} />Designation:</p>
                      <p style={{ fontSize: '1rem', fontWeight: '600', color: '#1a202c', margin: '0.25rem 0 0 0' }}>{developerdata.developerrole || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <p style={{ color: '#a0aec0' }}>No employee data available.</p>
                </div>
              )}
            </div>
          </div>

          {/* Attendance Table */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0.75rem',
            overflow: 'hidden',
            boxShadow: '0 4px 6px rgba(0,0,0,0.05)'
          }}>
            <div style={{
              padding: '1.5rem',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{
                fontSize: '1.25rem',
                fontWeight: '600',
                color: '#1a202c',
                margin: 0
              }}>Attendance History</h2>
              <div style={{
                display: 'flex',
                gap: '0.5rem'
              }}>
                <button
                  onClick={() => setFilterStatus("all")}
                  style={{
                    ...buttonStyle,
                    backgroundColor: filterStatus === "all" ? '#e2e8f0' : 'transparent',
                  }}
                >
                  All
                </button>
                <button
                  onClick={() => setFilterStatus("Present")}
                  style={{
                    ...buttonStyle,
                    backgroundColor: filterStatus === "Present" ? '#e2e8f0' : 'transparent',
                  }}
                >
                  Present
                </button>
                <button
                  onClick={() => setFilterStatus("Late")}
                  style={{
                    ...buttonStyle,
                    backgroundColor: filterStatus === "Late" ? '#e2e8f0' : 'transparent',
                  }}
                >
                  Late
                </button>
                <button
                  onClick={() => setFilterStatus("Absent")}
                  style={{
                    ...buttonStyle,
                    backgroundColor: filterStatus === "Absent" ? '#e2e8f0' : 'transparent',
                  }}
                >
                  Absent
                </button>
              </div>
            </div>

            <div style={{
              overflowX: 'auto'
            }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                textAlign: 'left'
              }}>
                <thead style={{
                  backgroundColor: '#f7fafc',
                  borderBottom: '1px solid #e2e8f0'
                }}>
                  <tr>
                    <th style={{ padding: '1rem', fontWeight: '600', color: '#718096', fontSize: '0.75rem', textTransform: 'uppercase' }}>Date</th>
                    <th style={{ padding: '1rem', fontWeight: '600', color: '#718096', fontSize: '0.75rem', textTransform: 'uppercase' }}>Status</th>
                    <th style={{ padding: '1rem', fontWeight: '600', color: '#718096', fontSize: '0.75rem', textTransform: 'uppercase' }}>Check-In</th>
                    <th style={{ padding: '1rem', fontWeight: '600', color: '#718096', fontSize: '0.75rem', textTransform: 'uppercase' }}>Check-Out</th>
                    <th style={{ padding: '1rem', fontWeight: '600', color: '#718096', fontSize: '0.75rem', textTransform: 'uppercase' }}>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="5" style={{ textAlign: 'center', padding: '2rem' }}>
                        <FaSpinner style={{
                          animation: 'spin 1s linear infinite',
                          color: '#4299e1',
                          fontSize: '2rem'
                        }} />
                      </td>
                    </tr>
                  ) : getStatusFilteredAttendances().length > 0 ? (
                    getStatusFilteredAttendances().map((att, index) => {
                      const statusColor = getStatusColor(att.developer_status);
                      return (
                       <tr key={index}>
  <td style={tdStyle}>
    {att.LoginDate ? format(parseISO(att.LoginDate), 'MMMM d, yyyy') : 'N/A'}
  </td>
  <td style={tdStyle}>
    <span style={{
      backgroundColor: statusColor.bg,
      color: statusColor.text,
      padding: '0.25rem 0.75rem',
      borderRadius: '9999px',
      fontWeight: '600',
      fontSize: '0.75rem'
    }}>
      {att.developer_status}
    </span>
  </td>
  <td style={tdStyle}>
    {/* Conditional rendering for login/logout vs. absentee dates */}
    {att.developer_status === 'Present' ? (
      att.login_in_time ? format(parseISO(att.login_in_time), 'h:mm a') : 'N/A'
    ) : (
      att.absentdatefrom ? format(parseISO(att.absentdatefrom), 'MMMM d, yyyy') : 'N/A'
    )}
  </td>
  <td style={tdStyle}>
    {att.developer_status === 'Present' ? (
      att.logout_out_time ? format(parseISO(att.logout_out_time), 'h:mm a') : 'N/A'
    ) : (
      att.absentdateto ? format(parseISO(att.absentdateto), 'MMMM d, yyyy') : 'N/A'
    )}
  </td>
  <td style={tdStyle}>
    <button
      onClick={() => openAttendanceDetails(att)}
      style={{
        padding: '0.25rem 0.75rem',
        backgroundColor: '#ebf8ff',
        color: '#2b6cb0',
        border: 'none',
        borderRadius: '0.25rem',
        cursor: 'pointer',
        fontSize: '0.75rem',
        fontWeight: '500'
      }}
    >
      View
    </button>
  </td>
</tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="5" style={{ textAlign: 'center', padding: '2rem', color: '#a0aec0' }}>
                        No attendance records found for this period.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

   {/* Detail Modal */}
      {showModal && selectedAttendance && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 100
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0.75rem',
            padding: '2rem',
            maxWidth: '600px',
            width: '90%',
            position: 'relative',
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
          }}>
            <button
              onClick={() => setShowModal(false)}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: '#4a5568',
                fontSize: '1.25rem'
              }}
            >
              <FaTimes />
            </button>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '1.5rem',
                paddingBottom: '1rem',
                borderBottom: '1px solid #e2e8f0'
            }}>
                <FaInfoCircle color="#4299e1" size={24} style={{ marginRight: '1rem' }} />
                <h3 style={{
                    fontSize: '1.5rem',
                    fontWeight: '600',
                    color: '#1a202c',
                    margin: 0
                }}>Attendance Details</h3>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                {/* Left Column */}
                <div style={{ display: 'grid', gap: '1rem' }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0.75rem',
                        borderRadius: '0.5rem',
                        backgroundColor: '#f7fafc',
                    }}>
                        <FaCalendarAlt color="#718096" style={{ marginRight: '1rem' }} />
                        <div>
                            <p style={{ color: '#718096', margin: 0, fontSize: '0.875rem' }}>Date:</p>
                            <p style={{ fontWeight: '500', color: '#1a202c', margin: 0 }}>
                                {selectedAttendance.LoginDate ? format(parseISO(selectedAttendance.LoginDate), 'MMMM d, yyyy') : 'N/A'}
                            </p>
                        </div>
                    </div>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0.75rem',
                        borderRadius: '0.5rem',
                        backgroundColor: '#f7fafc',
                    }}>
                        <FaCheckCircle color="#718096" style={{ marginRight: '1rem' }} />
                        <div>
                            <p style={{ color: '#718096', margin: 0, fontSize: '0.875rem' }}>Status:</p>
                            <p style={{ fontWeight: '500', color: '#1a202c', margin: 0 }}>{selectedAttendance.developer_status}</p>
                        </div>
                    </div>
                </div>

                {/* Right Column */}
                <div style={{ display: 'grid', gap: '1rem' }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0.75rem',
                        borderRadius: '0.5rem',
                        backgroundColor: '#f7fafc',
                    }}>
                        <FaSignInAlt color="#718096" style={{ marginRight: '1rem' }} />
                        <div>
                            <p style={{ color: '#718096', margin: 0, fontSize: '0.875rem' }}>Check-In Time:</p>
                           <p style={{ fontWeight: '500', color: '#1a202c', margin: 0 }}>
  {selectedAttendance.login_in_time
    ? format(parseISO(selectedAttendance.login_in_time), 'h:mm:ss a')
    : 'N/A'}
</p>

                        </div>
                    </div>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0.75rem',
                        borderRadius: '0.5rem',
                        backgroundColor: '#f7fafc',
                    }}>
                        <FaSignOutAlt color="#718096" style={{ marginRight: '1rem' }} />
                        <div>
                            <p style={{ color: '#718096', margin: 0, fontSize: '0.875rem' }}>Check-Out Time:</p>
                            <p style={{ fontWeight: '500', color: '#1a202c', margin: 0 }}>
                                {selectedAttendance.logout_out_time ? format(parseISO(selectedAttendance.logout_out_time), 'h:mm:ss a') : 'N/A'}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {selectedAttendance.developer_status === "Absent" && (
                <div style={{
                    marginTop: '1.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0.75rem',
                    borderRadius: '0.5rem',
                    backgroundColor: '#fff5f5',
                    border: '1px solid #fed7d7'
                }}>
                    <FaQuestionCircle color="#c53030" style={{ marginRight: '1rem' }} />
                    <div>
                        <p style={{ color: '#c53030', margin: 0, fontSize: '0.875rem' }}>Reason for Absence:</p>
                        <p style={{ fontWeight: '500', color: '#c53030', margin: 0 }}>{selectedAttendance.absentreson || 'N/A'}</p>
                    </div>
                </div>
            )}
            
            <div style={{ marginTop: '2rem', textAlign: 'right' }}>
                <button
                    onClick={() => setShowModal(false)}
                    style={{...modalButtonSecondaryStyle, minWidth: '100px'}}
                >
                    Close
                </button>
            </div>
          </div>
        </div>
      )}
      {/* Leave Request Modal */}
      {showLeaveModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 100
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0.75rem',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            position: 'relative',
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
          }}>
            <button
              onClick={() => setShowLeaveModal(false)}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: '#4a5568',
                fontSize: '1.25rem'
              }}
            >
              <FaTimes />
            </button>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '1.5rem',
                paddingBottom: '1rem',
                borderBottom: '1px solid #e2e8f0'
            }}>
                <FaCalendarPlus color="#da8132" size={24} style={{ marginRight: '1rem' }} />
                <h3 style={{
                    fontSize: '1.5rem',
                    fontWeight: '600',
                    color: '#1a202c',
                    margin: 0
                }}>Request Leave</h3>
            </div>
            <form onSubmit={handleLeaveSubmit}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', marginBottom: '1rem' }}>
  {/* Start Date */}
  <div style={{ flex: 1 }}>
    <label htmlFor="startDate" style={modalLabelStyle}>Start Date</label>
    <input
      id="startDate"
      type="date"
      name="startDate"
      value={leaveData.startDate}
      onChange={handleLeaveChange}
      style={modalInputStyle}
      required
    />
  </div>

  {/* End Date */}
  <div style={{ flex: 1 }}>
    <label htmlFor="endDate" style={modalLabelStyle}>End Date</label>
    <input
      id="endDate"
      type="date"
      name="endDate"
      value={leaveData.endDate}
      onChange={handleLeaveChange}
      style={modalInputStyle}
      required
    />
  </div>
</div>


              <div style={{ marginBottom: '1rem' }}>
                <label htmlFor="leaveType" style={modalLabelStyle}>Leave Type</label>
                <div style={{ position: 'relative' }}>
                    <select
                      id="leaveType"
                      name="type"
                      value={leaveData.type}
                      onChange={handleLeaveChange}
                      style={{
                        ...modalInputStyle,
                        WebkitAppearance: 'none',
                        MozAppearance: 'none',
                        appearance: 'none',
                        paddingRight: '2.5rem'
                      }}
                    >
                      <option>Sick Leave</option>
                      <option>Vacation</option>
                      <option>Other</option>
                    </select>
                    <div style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                        <FaEdit color="#a0aec0" />
                    </div>
                </div>
              </div>
              <div style={{ marginBottom: '1.5rem' }}>
                <label htmlFor="reason" style={modalLabelStyle}>Reason</label>
                <textarea
                  id="reason"
                  name="reason"
                  value={leaveData.reason}
                  onChange={handleLeaveChange}
                  rows="4"
                  style={{
                    ...modalInputStyle,
                    resize: 'vertical'
                  }}
                  required
                ></textarea>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                <button
                  type="button"
                  onClick={() => setShowLeaveModal(false)}
                  style={modalButtonSecondaryStyle}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    ...modalButtonPrimaryStyle,
                    background: loading ? '#a0aec0' : 'linear-gradient(135deg, #da8132 0%, #b7791f 100%)',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    boxShadow: loading ? 'none' : '0 4px 6px rgba(183, 121, 31, 0.3)'
                  }}
                >
                  {loading ? 'Submitting...' : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AttendancePage;